"use client";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Browser fingerprint — computed once, never re-derived
// ─────────────────────────────────────────────────────────────────────────────
function getBrowserInfo() {
  const ua = typeof navigator !== "undefined" ? navigator?.userAgent?.toLowerCase() : "";
  return {
    isFirefox: ua?.includes("firefox"),
    isSafari: ua?.includes("safari") && !ua?.includes("chrome"),
    isMac: /macintosh|mac os x/.test(ua),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Keyboard shortcuts that open DevTools
// ─────────────────────────────────────────────────────────────────────────────
// Each entry: { key, ctrl?, shift?, alt?, meta?, label }
const DEVTOOLS_SHORTCUTS = [
  // Universal
  { key: "F12", label: "F12" },
  // Chrome / Edge / Firefox  (Windows / Linux)
  { key: "I", ctrl: true, shift: true, label: "Ctrl+Shift+I" },
  { key: "J", ctrl: true, shift: true, label: "Ctrl+Shift+J" },
  { key: "C", ctrl: true, shift: true, label: "Ctrl+Shift+C" },
  // View source
  { key: "U", ctrl: true, label: "Ctrl+U" },
  // Mac equivalents (Cmd+Opt+I / J / C)
  { key: "I", meta: true, alt: true, label: "Cmd+Opt+I" },
  { key: "J", meta: true, alt: true, label: "Cmd+Opt+J" },
  { key: "C", meta: true, alt: true, label: "Cmd+Opt+C" },
];

function matchesShortcut(e, shortcut) {
  if (e.key.toUpperCase() !== shortcut.key.toUpperCase()) return false;
  if (shortcut.ctrl && !e.ctrlKey) return false;
  if (shortcut.shift && !e.shiftKey) return false;
  if (shortcut.alt && !e.altKey) return false;
  if (shortcut.meta && !e.metaKey) return false;
  // Negative checks — avoid false positives (e.g. Ctrl+I must NOT have meta)
  if (!shortcut.ctrl && e.ctrlKey && shortcut.key !== "F12") return false;
  if (!shortcut.meta && e.metaKey && shortcut.key !== "F12") return false;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Detection strategies
// ─────────────────────────────────────────────────────────────────────────────

/** Size-delta: works for docked DevTools in Chrome/Edge/Safari */
function checkSizeDelta(threshold = 160) {
  return (
    window.outerWidth - window.innerWidth > threshold ||
    window.outerHeight - window.innerHeight > threshold
  );
}

/**
 * Proxy toString trick: Chrome/Edge eagerly evaluate the object when the
 * console is open, triggering the getter — detects undocked panels too.
 * We swap console.log for exactly one call, then restore immediately.
 */
function checkConsoleTrick() {
  let triggered = false;
  const probe = new Proxy(Object.create(null), {
    get(_t, prop) {
      if (prop === "toString" || prop === Symbol.toPrimitive) triggered = true;
      return () => "";
    },
  });
  const orig = console.log; // save
  console.log = () => {}; // silence
  // eslint-disable-next-line no-console
  orig.call(console, "%c", probe); // call original directly — no infinite loop
  console.log = orig; // restore immediately
  return triggered;
}

/**
 * debugger-timing: Firefox pauses script execution when the DevTools panel
 * is open and a debugger statement is hit.
 */
function checkDebuggerTiming(thresholdMs = 100) {
  const t0 = performance.now();
  // eslint-disable-next-line no-debugger
  debugger;
  return performance.now() - t0 > thresholdMs;
}

// ─────────────────────────────────────────────────────────────────────────────
// useDevToolsDetection  — the core hook
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @typedef {Object} DetectionFlags
 * @property {boolean} [pollingEnabled=true]      - Size-delta / console-trick polling
 * @property {boolean} [keyboardEnabled=true]      - Keyboard shortcut detection
 * @property {boolean} [rightClickEnabled=true]    - Right-click → Inspect detection
 *
 * @typedef {Object} UseDevToolsOptions
 * @property {boolean}        [enabled=true]          - Master switch (your API flag).
 *                                                       false → all detection off, isOpen always false.
 * @property {DetectionFlags} [flags]                  - Granular feature flags.
 * @property {Function}       [onOpen]                 - Called once when DevTools open.
 * @property {Function}       [onClose]                - Called once when DevTools close.
 * @property {Function}       [onShortcut]             - Called with { label, event } on shortcut press.
 * @property {Function}       [onRightClick]           - Called with { event } on right-click.
 * @property {number}         [pollingInterval=1000]   - ms between polling checks.
 *
 * @returns {{ isOpen: boolean, triggerSource: string|null }}
 *   triggerSource: 'polling' | 'keyboard' | 'rightclick' | null
 */
export function useDevToolsDetection({
  enabled = true,
  flags: {
    pollingEnabled = true,
    keyboardEnabled = true,
    rightClickEnabled = true,
  } = {},
  onOpen,
  onClose,
  onShortcut,
  onRightClick,
  pollingInterval = 1000,
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerSource, setTrigger] = useState(null);

  // Stable refs so effect deps don't change when callers pass inline functions
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onShortcutRef = useRef(onShortcut);
  const onRightClickRef = useRef(onRightClick);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onShortcutRef.current = onShortcut;
  }, [onShortcut]);
  useEffect(() => {
    onRightClickRef.current = onRightClick;
  }, [onRightClick]);

  // Track previous open state without triggering re-renders
  const prevOpenRef = useRef(false);

  // Computed once
  const browser = useMemo(() => getBrowserInfo(), []);

  // ── Mark open/closed with source ────────────────────────────────────────
  const markOpen = useCallback((source) => {
    setIsOpen(true);
    setTrigger(source);
    if (!prevOpenRef.current) {
      prevOpenRef.current = true;
      onOpenRef.current?.();
    }
  }, []);

  const markClosed = useCallback(() => {
    if (prevOpenRef.current) {
      setIsOpen(false);
      setTrigger(null);
      prevOpenRef.current = false;
      onCloseRef.current?.();
    }
  }, []);

  // ── Composite poll check ─────────────────────────────────────────────────
  const pollCheck = useCallback(() => {
    if (browser.isFirefox) return checkDebuggerTiming();
    if (browser.isSafari) return checkSizeDelta();
    return checkSizeDelta() || checkConsoleTrick();
  }, [browser]);

  // ── 1. Polling (size-delta + console trick) ──────────────────────────────
  useEffect(() => {
    if (!enabled || !pollingEnabled) return;

    let hidden = document.hidden; // pause when tab not visible

    const onVisibility = () => {
      hidden = document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibility);

    const id = setInterval(() => {
      if (hidden) return; // skip when tab is backgrounded
      if (pollCheck()) markOpen("polling");
      else markClosed();
    }, pollingInterval);

    // Immediate first check
    if (pollCheck()) markOpen("polling");
    else markClosed();

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollingEnabled, pollingInterval]); // pollCheck/markOpen/markClosed are stable

  // ── 2. Keyboard shortcut detection ──────────────────────────────────────
  useEffect(() => {
    if (!enabled || !keyboardEnabled) return;

    const handler = (e) => {
      const hit = DEVTOOLS_SHORTCUTS.find((s) => matchesShortcut(e, s));
      if (!hit) return;
      // Don't call preventDefault — we only observe, not block.
      markOpen("keyboard");
      onShortcutRef.current?.({ label: hit.label, event: e });
    };

    window.addEventListener("keydown", handler, { capture: true });
    return () =>
      window.removeEventListener("keydown", handler, { capture: true });
  }, [enabled, keyboardEnabled, markOpen]);

  // ── 3. Right-click → Inspect ─────────────────────────────────────────────
  // We can't know if the user picks "Inspect" from the context menu, but we
  // can flag that a right-click occurred (which is the gateway to Inspect).
  // Set a short timeout: if DevTools open within ~2 s of right-click, the
  // triggerSource will already be 'rightclick'.
  useEffect(() => {
    if (!enabled || !rightClickEnabled) return;

    let pendingTimer = null;

    const handler = (e) => {
      onRightClickRef.current?.({ event: e });
      // Pre-arm: mark as potential right-click open for next poll cycle
      pendingTimer = setTimeout(() => {
        if (pollCheck()) markOpen("rightclick");
      }, 300); // context menu actions take ~200-400 ms
    };

    window.addEventListener("contextmenu", handler, { capture: true });
    return () => {
      window.removeEventListener("contextmenu", handler, { capture: true });
      if (pendingTimer) clearTimeout(pendingTimer);
    };
  }, [enabled, rightClickEnabled, pollCheck, markOpen]);

  // ── Reset everything when master switch turns off ────────────────────────
  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
      setTrigger(null);
      prevOpenRef.current = false;
    }
  }, [enabled]);

  return { isOpen, triggerSource };
}


